<script setup>
import { ref, computed, onMounted } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import axios from 'axios'
import { useCart } from '../stores/cartStore'
import { storeToRefs } from 'pinia'
import { useCurrencyStore } from '../stores/currencyStore'
import { useLegalLinks } from '../router/legalLinks'

const API_URL = import.meta.env.VITE_API_URL
const router = useRouter()
const legalPaths = useLegalLinks()
const showSteamRequiredModal = ref(false)
const { items, totalPrice, removeItem, clearCart, increaseQuantity, decreaseQuantity, updateQuantity } = useCart()

const onQtyInput = (itemId, event) => {
  const val = parseInt(event.target.value)
  if (val >= 1 && val <= 999) {
    updateQuantity(itemId, val)
  } else if (val < 1 || isNaN(val)) {
    updateQuantity(itemId, 1)
    event.target.value = 1
  }
}
const currencyStore = useCurrencyStore()
const { symbol, rate, selectedCode, selectedCurrency } = storeToRefs(currencyStore)

const user = ref(null)
const purchasing = ref(false)
const purchaseResult = ref(null)
const showSuccessModal = ref(false)
const purchasedCount = ref(0)

const showCheckoutModal = ref(false)
const checkoutAgreed = ref(false)
const deliveryName = ref('')
const checkoutError = ref('')
const paymentConfig = ref(null)
const paymentConfigError = ref('')
const tokensPerEur = computed(() => {
  const value = Number(paymentConfig.value?.tokensPerEur)
  return value > 0 ? value : null
})
const minimumCurrencyAmount = computed(() => {
  const configured = Number(selectedCurrency.value?.minDeposit)
  if (configured > 0) return configured
  const minimumEur = Number(paymentConfig.value?.minDepositEur)
  const selectedRate = Number(rate.value)
  return minimumEur > 0 && selectedRate > 0 ? minimumEur * selectedRate : null
})
const minimumCartCredits = computed(() => {
  const minimum = Number(minimumCurrencyAmount.value)
  const selectedRate = Number(rate.value)
  const tokens = Number(tokensPerEur.value)
  if (minimum <= 0 || selectedRate <= 0 || tokens <= 0) return null
  return Math.round(((minimum / selectedRate) * tokens) * 100) / 100
})
const cartMeetsMinimum = computed(() => (
  Number(minimumCartCredits.value) > 0
  && Math.round(Number(totalPrice.value) * 100) >= Math.round(minimumCartCredits.value * 100)
))
const minimumCartMessage = computed(() => {
  if (!(Number(minimumCartCredits.value) > 0)) return paymentConfigError.value || 'Minimum cart total is temporarily unavailable.'
  const remaining = Math.max(0, minimumCartCredits.value - Number(totalPrice.value))
  return `Minimum cart total is ${minimumCartCredits.value.toFixed(2)} ArkGem (${symbol.value}${minimumCurrencyAmount.value.toFixed(2)}). Add ${remaining.toFixed(2)} more to continue.`
})
const paymentMethods = ref([])
const filteredPaymentMethods = computed(() => paymentMethods.value.filter((method) => {
  const allowed = Array.isArray(method.currencies) ? method.currencies : []
  return allowed.length === 0 || allowed.includes(selectedCode.value)
}))
const initiating = ref(false)
const checkoutForm = ref({
  firstName: '',
  lastName: '',
  email: '',
  country: '',
  city: '',
  address: '',
  zipCode: '',
  phone: ''
})

const openLoginModal = () => {
  window.dispatchEvent(new Event('open-login'))
}

const steamLinkUrl = computed(() => {
  const token = (typeof window !== 'undefined' && window.localStorage)
    ? window.localStorage.getItem('steam_token') || ''
    : ''
  return `${API_URL}/api/auth/steam/link?token=${encodeURIComponent(token)}`
})

onMounted(async () => {
  await currencyStore.fetchCurrencies()
  const token = localStorage.getItem('steam_token')
  if (!token) {
    clearCart()
    router.replace('/')
    openLoginModal()
    return
  }
  try {
    const response = await axios.get(`${API_URL}/api/auth/user`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    if (response.data.user) {
      user.value = response.data.user
    } else {
      localStorage.removeItem('steam_token')
      clearCart()
      router.replace('/')
      openLoginModal()
    }
  } catch (e) {
    localStorage.removeItem('steam_token')
    clearCart()
    router.replace('/')
    openLoginModal()
  }

  try {
    const response = await axios.get(`${API_URL}/api/payments/config`)
    paymentConfig.value = response.data
  } catch (e) {
    paymentConfig.value = null
    paymentConfigError.value = 'Minimum cart configuration is temporarily unavailable.'
  }

  try {
    const resp = await axios.get(`${API_URL}/api/payments/checkout-methods`)
    if (Array.isArray(resp.data?.methods)) {
      paymentMethods.value = resp.data.methods
    }
  } catch (e) {}
})

const canAfford = computed(() => {
  if (!user.value) return false
  const balance = parseFloat(user.value.balance) || 0
  const total = parseFloat(totalPrice.value) || 0
  return balance >= total
})
const needsDeliveryName = computed(() => Boolean(user.value) && !user.value?.eosId)

const displayTotal = computed(() => {
  const total = parseFloat(totalPrice.value) || 0
  return `${symbol.value}${(total * Number(rate.value || 1)).toFixed(2)}`
})

const openCheckoutModal = () => {
  if (!user.value) {
    openLoginModal()
    return
  }
  if (items.value.length === 0) return
  if (!cartMeetsMinimum.value) {
    purchaseResult.value = { success: false, message: minimumCartMessage.value }
    return
  }
  checkoutError.value = ''
  showCheckoutModal.value = true
}

const closeCheckoutModal = () => {
  showCheckoutModal.value = false
  checkoutAgreed.value = false
  deliveryName.value = ''
  checkoutError.value = ''
  checkoutForm.value = {
    firstName: '',
    lastName: '',
    email: '',
    country: '',
    city: '',
    address: '',
    zipCode: '',
    phone: ''
  }
}

const canSubmitCheckout = computed(() => {
  if (!cartMeetsMinimum.value) return false
  if (!checkoutAgreed.value) return false
  if (!checkoutForm.value.firstName.trim()) return false
  if (!checkoutForm.value.lastName.trim()) return false
  if (!checkoutForm.value.email.trim()) return false
  if (!checkoutForm.value.country.trim()) return false
  if (!checkoutForm.value.city.trim()) return false
  if (!checkoutForm.value.address.trim()) return false
  if (needsDeliveryName.value && !deliveryName.value.trim()) return false
  return true
})

const payWithBalance = async () => {
  if (purchasing.value) return
  if (!user.value || items.value.length === 0) return
  if (!canAfford.value) {
    checkoutError.value = 'Insufficient balance'
    return
  }

  await checkout()
}

// Pay via an external payment gateway for the cart total. Initiates a hosted
// payment using the chosen gateway's credentials and redirects the user to it.
const payWithGateway = async (method) => {
  if (initiating.value) return
  if (!canSubmitCheckout.value) {
    checkoutError.value = 'Please complete the contact information'
    return
  }
  if (!user.value || items.value.length === 0) return

  initiating.value = true
  checkoutError.value = ''
  const token = localStorage.getItem('steam_token')
  try {
    const total = parseFloat(parseFloat(totalPrice.value).toFixed(2))
    const response = await axios.post(`${API_URL}/api/payments/initiate`, {
      customAmount: total,
      gatewayId: method.id,
      billingInfo: { ...checkoutForm.value },
    }, { headers: { Authorization: `Bearer ${token}` } })

    if (response.data?.success && response.data.redirectUrl) {
      window.location.href = response.data.redirectUrl
    } else {
      checkoutError.value = response.data?.message || 'Failed to initiate payment'
    }
  } catch (e) {
    checkoutError.value = e.response?.data?.message || 'Failed to initiate payment'
  } finally {
    initiating.value = false
  }
}

const checkout = async () => {
  if (purchasing.value) return
  if (!user.value || items.value.length === 0) return
  
  const balance = parseFloat(user.value.balance) || 0
  const total = parseFloat(totalPrice.value) || 0
  
  if (balance < total) {
    checkoutError.value = 'Insufficient balance'
    return
  }
  
  purchasing.value = true
  checkoutError.value = ''
  
  const token = localStorage.getItem('steam_token')
  let successCount = 0
  const failedItems = []
  
  for (const item of items.value) {
    const qty = item.quantity || 1
    try {
      const resp = await axios.post(`${API_URL}/api/shop/purchase`, 
        {
          productId: item.id,
          quantity: qty,
          deliveryName: needsDeliveryName.value ? deliveryName.value.trim() : ''
        },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      if (resp.data.refunded) {
        failedItems.push(item.name)
      } else {
        successCount += qty
      }
      // Update balance from server response
      if (resp.data.newBalance !== undefined && user.value) {
        user.value.balance = resp.data.newBalance
      }
    } catch (e) {
      const msg = e.response?.data?.message || e.response?.data?.error || ''
      if (msg.includes('Insufficient balance')) {
        failedItems.push(`${item.name} (insufficient balance)`)
        break // No point continuing if balance ran out
      }
      failedItems.push(item.name)
    }
  }
  
  purchasing.value = false
  
  if (successCount > 0 && failedItems.length === 0) {
    // All items succeeded
    purchasedCount.value = successCount
    closeCheckoutModal()
    showSuccessModal.value = true
    clearCart()
  } else if (successCount > 0 && failedItems.length > 0) {
    // Partial success
    purchasedCount.value = successCount
    closeCheckoutModal()
    showSuccessModal.value = true
    clearCart()
    // Show which items failed after a short delay
    setTimeout(() => {
      purchaseResult.value = { 
        success: false, 
        message: `${successCount} item(s) purchased. Unavailable (refunded): ${failedItems.join(', ')}` 
      }
    }, 100)
  } else {
    // All failed
    purchaseResult.value = { 
      success: false, 
      message: `Items unavailable: ${failedItems.join(', ')}. Balance refunded.` 
    }
  }
}

const openTopUp = () => {
  window.dispatchEvent(new CustomEvent('open-topup'))
}
</script>

<template>
  <main class="cart-main">
      <div class="cart-container">
        <div class="cart-header">
          <h1 class="cart-title">CART</h1>
          <span class="cart-count">{{ items.length }} item(s)</span>
        </div>

        <div class="cart-layout" v-if="items.length > 0">
          <!-- Cart Items -->
          <div class="cart-items">
            <div v-for="item in items" :key="item.id" class="cart-item">
              <div class="item-image">
                <img v-if="item.image" :src="item.image" :alt="item.name" />
                <div v-else class="item-placeholder">
                  <svg viewBox="0 0 24 24"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/></svg>
                </div>
              </div>
              <div class="item-info">
                <h3 class="item-name">{{ item.name }}</h3>
                <span class="item-category">{{ item.category }}</span>
              </div>
              
              <div class="quantity-control">
                <template v-if="item.arkDeliveryType === 'creature'">
                  <span class="qty-fixed">1</span>
                </template>
                <template v-else>
                  <button class="qty-btn" @click="decreaseQuantity(item.id)">−</button>
                  <input 
                    type="number" 
                    class="qty-input" 
                    :value="item.quantity || 1" 
                    min="1" 
                    max="999"
                    @change="onQtyInput(item.id, $event)"
                    @focus="$event.target.select()"
                  />
                  <button class="qty-btn" @click="increaseQuantity(item.id)">+</button>
                </template>
              </div>
              
              <div class="item-price">
                <span class="price-value">{{ parseFloat((item.price * (item.quantity || 1)).toFixed(2)) }}</span>
                <span class="price-currency">ArkGem</span>
              </div>
              <button class="btn-remove" @click="removeItem(item.id)">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                </svg>
              </button>
            </div>

            <button class="btn-clear" @click="clearCart">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
              </svg>
              Clear Cart
            </button>
          </div>

          <!-- Payment Section -->
          <div class="payment-section">
            <div class="payment-card">
              <div class="payment-corners">
                <span class="corner tl"></span>
                <span class="corner tr"></span>
                <span class="corner bl"></span>
                <span class="corner br"></span>
              </div>
              
              <h2 class="payment-title">PAYMENT</h2>
              
              <div class="payment-total">
                <span class="total-label">TOTAL:</span>
                <div class="total-amount">
                  <span class="total-value">{{ parseFloat(totalPrice.toFixed(2)) }}</span>
                  <span class="total-currency">ArkGem</span>
                </div>
              </div>

              <div v-if="purchaseResult && !purchaseResult.success" class="purchase-result error">
                {{ purchaseResult.message }}
              </div>

              <div v-if="items.length > 0 && !cartMeetsMinimum" class="purchase-result error" role="status">
                {{ minimumCartMessage }}
              </div>

              <button 
                v-if="user" 
                class="btn-checkout" 
                @click="openCheckoutModal"
                :disabled="items.length === 0 || !cartMeetsMinimum"
              >
                BUY NOW
              </button>
              <button v-else type="button" class="btn-checkout btn-login" @click="openLoginModal">
                LOGIN TO PURCHASE
              </button>

              <div class="balance-info" v-if="user">
                <span class="balance-label">MY BALANCE:</span>
                <div class="balance-row">
                  <span class="balance-value">{{ parseFloat(Number(user.balance).toFixed(2)) }} ArkGem</span>
                  <button class="btn-topup" @click="openTopUp">TOP UP</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty Cart -->
        <div class="empty-cart" v-else>
          <div class="empty-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
              <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/>
            </svg>
          </div>
          <h2 class="empty-title">YOUR CART IS EMPTY</h2>
          <p class="empty-desc">Add items from the market to get started</p>
          <RouterLink to="/market" class="btn-shop">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17"/>
            </svg>
            GO TO MARKET
          </RouterLink>
        </div>
      </div>
    </main>

    <!-- Success Modal -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showSuccessModal" class="success-overlay" @click.self="showSuccessModal = false">
          <div class="success-modal">
            <div class="success-glow"></div>
            <div class="success-corners">
              <span class="corner tl"></span>
              <span class="corner tr"></span>
              <span class="corner bl"></span>
              <span class="corner br"></span>
            </div>
            
            <div class="success-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            
            <h2 class="success-title">PURCHASE COMPLETE</h2>
            <p class="success-message">Successfully purchased <span class="highlight">{{ purchasedCount }}</span> item(s)!</p>
            <p class="success-sub">Online players receive items immediately; offline deliveries remain pending until they join.</p>
            
            <div class="success-actions">
              <RouterLink to="/market" class="btn-continue">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17"/>
                </svg>
                CONTINUE SHOPPING
              </RouterLink>
              <RouterLink to="/profile" class="btn-profile">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
                VIEW ORDERS
              </RouterLink>
            </div>
            
            <button class="btn-close-modal" @click="showSuccessModal = false">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Checkout Modal -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showCheckoutModal" class="checkout-overlay" @click.self="closeCheckoutModal">
          <div class="checkout-modal">
            <div class="checkout-head">
              <button type="button" class="checkout-back" @click="closeCheckoutModal">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M19 12H5M12 19l-7-7 7-7"/>
                </svg>
              </button>
              <div class="checkout-head-title">ENTER CONTACT INFORMATION</div>
              <button type="button" class="checkout-x" @click="closeCheckoutModal">×</button>
            </div>

            <div class="checkout-grid">
              <div class="checkout-field">
                <label>First name:</label>
                <input v-model="checkoutForm.firstName" type="text" class="checkout-input" placeholder="Your first name" />
              </div>
              <div class="checkout-field">
                <label>Last name:</label>
                <input v-model="checkoutForm.lastName" type="text" class="checkout-input" placeholder="Your last name" />
              </div>
              <div class="checkout-field">
                <label>Phone:</label>
                <input v-model="checkoutForm.phone" type="text" class="checkout-input" placeholder="Your phone number" />
              </div>
              <div class="checkout-field">
                <label>Email:</label>
                <input v-model="checkoutForm.email" type="email" class="checkout-input" placeholder="Your email" />
              </div>
              <div class="checkout-field">
                <label>Address:</label>
                <input v-model="checkoutForm.address" type="text" class="checkout-input" placeholder="Your address" />
              </div>
              <div class="checkout-field">
                <label>City:</label>
                <input v-model="checkoutForm.city" type="text" class="checkout-input" placeholder="Your city" />
              </div>
              <div class="checkout-field">
                <label>Country:</label>
                <input v-model="checkoutForm.country" type="text" class="checkout-input" placeholder="Your country" />
              </div>
              <div class="checkout-field">
                <label>ZIP Code:</label>
                <input v-model="checkoutForm.zipCode" type="text" class="checkout-input" placeholder="Your zip code" />
              </div>
              <div v-if="needsDeliveryName" class="checkout-field">
                <label>In-game nickname:</label>
                <input v-model="deliveryName" type="text" maxlength="100" class="checkout-input" placeholder="Your exact in-game name" autocomplete="off" />
              </div>
            </div>

            <label class="checkout-agree">
              <input type="checkbox" v-model="checkoutAgreed" />
              <span class="check"></span>
              <span class="text">
                I agree to the
                <a :href="legalPaths.privacy" target="_blank" rel="noopener noreferrer" @click.stop>Privacy Notice</a>
                and
                <a :href="legalPaths.terms" target="_blank" rel="noopener noreferrer" @click.stop>Terms of Use</a>.
              </span>
            </label>

            <div class="checkout-actions">
              <div class="checkout-total">TOTAL: {{ displayTotal }} / {{ parseFloat(totalPrice.toFixed(2)) }} ArkGem</div>

              <div class="checkout-methods" v-if="filteredPaymentMethods.length">
                <button
                  v-for="method in filteredPaymentMethods"
                  :key="method.id"
                  type="button"
                  class="checkout-pay"
                  :disabled="!canSubmitCheckout || initiating"
                  @click="payWithGateway(method)"
                >
                  <span v-if="method.logos && method.logos.length" class="checkout-method-logos">
                    <img v-for="(logo, i) in method.logos" :key="i" :src="logo" :alt="method.name" class="checkout-method-logo" />
                  </span>
                  <span class="checkout-method-name">{{ initiating ? 'PROCESSING...' : method.name }}</span>
                </button>
              </div>
              <button v-else class="checkout-pay" :disabled="!canSubmitCheckout">CARD</button>

              <div v-if="!canAfford" class="checkout-insufficient">
                There are insufficient funds in your balance.
              </div>
              <div v-if="needsDeliveryName" class="checkout-insufficient">
                If you are offline, the order stays pending until you join with this exact name.
              </div>

              <div class="checkout-divider">or</div>

              <button 
                class="checkout-balance" 
                :disabled="!canAfford || !canSubmitCheckout || purchasing"
                @click="payWithBalance"
              >
                {{ purchasing ? 'PROCESSING...' : 'PAY WITH BALANCE' }}
              </button>

              <div v-if="checkoutError" class="checkout-error">{{ checkoutError }}</div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Steam Required Modal -->
    <Teleport to="body">
      <div v-if="showSteamRequiredModal" class="steam-overlay" @click.self="showSteamRequiredModal = false">
        <div class="steam-modal">
          <button type="button" class="steam-modal-close" @click="showSteamRequiredModal = false">×</button>

          <div class="steam-icon-wrap">
            <div class="steam-icon-glow"></div>
            <img src="/download.svg" alt="Steam" class="steam-icon" />
          </div>

          <h2 class="steam-modal-title">Steam Account Required</h2>
          <p class="steam-modal-desc">
            To purchase items, connect your Steam account so items can be delivered directly to your in-game inventory.
          </p>

          <div class="steam-modal-actions">
            <a :href="steamLinkUrl" class="steam-modal-confirm">
              <img src="/download.svg" alt="" class="btn-steam-icon" aria-hidden="true" />
              <span>Connect Steam</span>
            </a>
            <button type="button" class="steam-modal-cancel" @click="showSteamRequiredModal = false">Maybe Later</button>
          </div>
        </div>
      </div>
    </Teleport>
</template>

<style scoped>
.cart-main {
  padding-top: 120px;
  padding-bottom: 80px;
  min-height: 100vh;
}

.cart-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 40px;
}

.cart-header {
  text-align: center;
  margin-bottom: 50px;
}

.cart-title {
  font-family: var(--font-display);
  font-size: 4rem;
  color: #fff;
  letter-spacing: 8px;
  margin-bottom: 10px;
  text-shadow: 0 0 40px rgba(0, 212, 255, 0.3);
}

.cart-count {
  color: rgba(255, 255, 255, 0.5);
  font-size: 1.1rem;
  letter-spacing: 2px;
}

.cart-layout {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 40px;
  align-items: start;
}

/* Cart Items */
.cart-items {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.cart-item {
  display: flex;
  align-items: center;
  gap: 20px;
  background: linear-gradient(135deg, rgba(10, 15, 25, 0.9) 0%, rgba(5, 10, 18, 0.95) 100%);
  border: 1px solid rgba(0, 212, 255, 0.15);
  border-radius: 16px;
  padding: 18px 24px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.cart-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.3), transparent);
}

.cart-item:hover {
  border-color: rgba(0, 212, 255, 0.35);
  transform: translateX(5px);
  box-shadow: 0 5px 30px rgba(0, 212, 255, 0.1);
}

.item-image {
  width: 80px;
  height: 80px;
  border-radius: 14px;
  overflow: hidden;
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(0, 150, 200, 0.05) 100%);
  flex-shrink: 0;
  border: 1px solid rgba(0, 212, 255, 0.2);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

.item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.item-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.item-placeholder svg {
  width: 35px;
  height: 35px;
  fill: none;
  stroke: rgba(0, 212, 255, 0.4);
  stroke-width: 1.5;
}

.item-info {
  flex: 1;
  min-width: 0;
}

.item-name {
  font-family: var(--font-display);
  font-size: 1.1rem;
  color: #fff;
  margin-bottom: 5px;
  text-transform: uppercase;
  letter-spacing: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-category {
  font-size: 0.85rem;
  color: var(--color-primary);
  text-transform: uppercase;
  letter-spacing: 1px;
}

.item-price {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-right: 20px;
}

.price-value {
  font-family: var(--font-display);
  font-size: 1.4rem;
  color: #fff;
  font-weight: 700;
}

.price-currency {
  font-size: 0.8rem;
  color: var(--color-primary);
  font-weight: 600;
}

.quantity-control {
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.08) 0%, rgba(0, 150, 200, 0.05) 100%);
  border: 1px solid rgba(0, 212, 255, 0.25);
  border-radius: 30px;
  padding: 5px 8px;
}

.qty-btn {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.15) 0%, rgba(0, 180, 220, 0.1) 100%);
  border: 1px solid rgba(0, 212, 255, 0.4);
  border-radius: 50%;
  color: var(--color-primary);
  font-size: 1.3rem;
  font-weight: 400;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.25s ease;
  text-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
}

.qty-btn:hover {
  background: linear-gradient(135deg, var(--color-primary) 0%, #0095b3 100%);
  border-color: var(--color-primary);
  color: #000;
  transform: scale(1.15);
  box-shadow: 0 0 20px rgba(0, 212, 255, 0.5);
  text-shadow: none;
}

.qty-fixed {
  font-family: var(--font-display);
  font-size: 1.3rem;
  color: rgba(0, 212, 255, 0.7);
  width: 40px;
  text-align: center;
}

.qty-input {
  font-family: var(--font-display);
  font-size: 1.3rem;
  color: #fff;
  width: 50px;
  text-align: center;
  background: transparent;
  border: none;
  outline: none;
  text-shadow: 0 0 15px rgba(0, 212, 255, 0.3);
  -moz-appearance: textfield;
}

.qty-input::-webkit-outer-spin-button,
.qty-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.qty-input:focus {
  background: rgba(0, 212, 255, 0.08);
  border-radius: 6px;
}

.btn-remove {
  width: 44px;
  height: 44px;
  background: rgba(255, 80, 80, 0.08);
  border: 1px solid rgba(255, 80, 80, 0.25);
  border-radius: 12px;
  color: #ff6b6b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.btn-remove svg {
  width: 18px;
  height: 18px;
}

.btn-remove:hover {
  background: rgba(255, 80, 80, 0.2);
  border-color: #ff6b6b;
  transform: scale(1.1);
  box-shadow: 0 0 20px rgba(255, 100, 100, 0.3);
}

.btn-clear {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;
  padding: 14px 24px;
  background: transparent;
  border: 1px dashed rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.9rem;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-clear svg {
  width: 16px;
  height: 16px;
}

.btn-clear:hover {
  border-color: rgba(255, 100, 100, 0.3);
  color: #ff6b6b;
}

/* Payment Section */
.payment-section {
  position: sticky;
  top: 120px;
}

.payment-card {
  background: linear-gradient(165deg, rgba(12, 18, 30, 0.95) 0%, rgba(5, 10, 20, 0.98) 100%);
  border: 1px solid rgba(0, 212, 255, 0.15);
  border-radius: 20px;
  padding: 35px;
  position: relative;
}

.payment-corners {
  position: absolute;
  inset: 12px;
  pointer-events: none;
}

.corner {
  position: absolute;
  width: 15px;
  height: 15px;
  border: 2px solid rgba(0, 212, 255, 0.3);
}

.corner.tl { top: 0; left: 0; border-right: none; border-bottom: none; }
.corner.tr { top: 0; right: 0; border-left: none; border-bottom: none; }
.corner.bl { bottom: 0; left: 0; border-right: none; border-top: none; }
.corner.br { bottom: 0; right: 0; border-left: none; border-top: none; }

.payment-title {
  font-family: var(--font-display);
  font-size: 1.8rem;
  color: #fff;
  text-align: center;
  margin-bottom: 25px;
  letter-spacing: 4px;
}

.payment-total {
  text-align: center;
  margin-bottom: 25px;
  padding: 20px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 12px;
}

.total-label {
  display: block;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.5);
  letter-spacing: 2px;
  margin-bottom: 8px;
}

.total-amount {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 8px;
}

.total-value {
  font-family: var(--font-display);
  font-size: 2.5rem;
  color: #fff;
  font-weight: 700;
  text-shadow: 0 0 20px rgba(0, 212, 255, 0.3);
}

.total-currency {
  font-size: 1rem;
  color: var(--color-primary);
  font-weight: 600;
}

.purchase-result {
  text-align: center;
  padding: 12px;
  border-radius: 10px;
  margin-bottom: 20px;
  font-size: 0.9rem;
}

.purchase-result.success {
  background: rgba(0, 255, 100, 0.1);
  border: 1px solid rgba(0, 255, 100, 0.3);
  color: #00ff64;
}

.purchase-result.error {
  background: rgba(255, 100, 100, 0.1);
  border: 1px solid rgba(255, 100, 100, 0.3);
  color: #ff6b6b;
}

.btn-checkout {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 18px;
  background: linear-gradient(135deg, var(--color-primary) 0%, #0095b3 100%);
  color: #000;
  border: none;
  border-radius: 14px;
  font-weight: 800;
  font-size: 1.1rem;
  letter-spacing: 3px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 6px 30px rgba(0, 212, 255, 0.3);
  text-decoration: none;
}

.btn-checkout:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 10px 40px rgba(0, 212, 255, 0.4);
}

.btn-checkout:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-login {
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  color: #fff;
  border: 1px solid rgba(0, 212, 255, 0.3);
}

.balance-info {
  margin-top: 25px;
  padding-top: 25px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;
}

.balance-label {
  display: block;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.5);
  letter-spacing: 2px;
  margin-bottom: 10px;
}

.balance-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
}

.balance-value {
  font-family: var(--font-display);
  font-size: 1.3rem;
  color: #fff;
}

.btn-topup {
  padding: 8px 20px;
  background: linear-gradient(135deg, var(--color-primary) 0%, #0095b3 100%);
  color: #000;
  border: none;
  border-radius: 20px;
  font-weight: 700;
  font-size: 0.8rem;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-topup:hover {
  transform: scale(1.05);
}

/* Empty Cart */
.empty-cart {
  text-align: center;
  padding: 60px 40px;
  max-width: 500px;
  margin: 0 auto;
  background: linear-gradient(165deg, rgba(10, 15, 25, 0.85) 0%, rgba(5, 10, 20, 0.92) 100%);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(0, 212, 255, 0.12);
  border-radius: 24px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4), 0 0 40px rgba(0, 212, 255, 0.05);
}

.empty-cart::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.4), transparent);
}

.empty-cart::after {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle at 50% 0%, rgba(0, 212, 255, 0.06) 0%, transparent 50%);
  pointer-events: none;
}

.empty-icon {
  width: 100px;
  height: 100px;
  margin: 0 auto 25px;
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(0, 180, 220, 0.05) 100%);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
  animation: emptyPulse 3s ease-in-out infinite;
}

@keyframes emptyPulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(0, 212, 255, 0.1); }
  50% { box-shadow: 0 0 30px 5px rgba(0, 212, 255, 0.1); }
}

.empty-icon svg {
  width: 45px;
  height: 45px;
  stroke: rgba(0, 212, 255, 0.5);
}

.empty-title {
  font-family: var(--font-display);
  font-size: 1.8rem;
  color: #fff;
  margin-bottom: 10px;
  letter-spacing: 3px;
  position: relative;
  z-index: 1;
}

.empty-desc {
  color: rgba(255, 255, 255, 0.45);
  margin-bottom: 30px;
  font-size: 0.95rem;
  position: relative;
  z-index: 1;
}

.btn-shop {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 32px;
  background: linear-gradient(135deg, var(--color-primary) 0%, #0095b3 100%);
  color: #000;
  border: none;
  border-radius: 14px;
  font-weight: 700;
  font-size: 0.95rem;
  letter-spacing: 2px;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 6px 25px rgba(0, 212, 255, 0.25);
  position: relative;
  z-index: 1;
}

.btn-shop svg {
  width: 18px;
  height: 18px;
}

.btn-shop:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 40px rgba(0, 212, 255, 0.4);
  filter: brightness(1.1);
}

@media (max-width: 900px) {
  .cart-layout {
    grid-template-columns: 1fr;
  }
  
  .payment-section {
    position: static;
  }
  
  .cart-title {
    font-size: 3rem;
  }
}

@media (max-width: 768px) {
  .cart-main {
    padding-top: 100px;
    padding-bottom: 60px;
  }
  
  .cart-container {
    padding: 0 20px;
  }
  
  .cart-header {
    margin-bottom: 30px;
  }
  
  .cart-title {
    font-size: 2.5rem;
    letter-spacing: 4px;
  }
  
  .cart-item {
    flex-wrap: wrap;
    padding: 22px;
    gap: 18px;
    border-radius: 18px;
  }
  
  .item-image {
    width: 80px;
    height: 80px;
    border-radius: 14px;
  }
  
  .item-info {
    flex: 1;
    min-width: calc(100% - 100px);
  }
  
  .item-name {
    font-size: 1.2rem;
    margin-bottom: 6px;
  }
  
  .item-category {
    font-size: 0.9rem;
  }
  
  .quantity-control {
    order: 3;
    flex: none;
    padding: 8px 12px;
    gap: 10px;
  }
  
  .qty-btn {
    width: 38px;
    height: 38px;
    font-size: 1.3rem;
  }
  
  .qty-value {
    font-size: 1.2rem;
    min-width: 35px;
  }
  
  .item-price {
    order: 4;
    margin-left: auto;
  }
  
  .price-value {
    font-size: 1.25rem;
  }
  
  .btn-remove {
    order: 5;
    width: 40px;
    height: 40px;
  }
  
  .payment-card {
    padding: 25px;
  }
  
  .payment-title {
    font-size: 1.5rem;
  }
  
  .total-value {
    font-size: 2rem;
  }
  
  .empty-cart {
    padding: 40px 25px;
    margin: 0 20px;
  }
  
  .empty-icon {
    width: 100px;
    height: 100px;
  }
  
  .empty-icon svg {
    width: 50px;
    height: 50px;
  }
  
  .empty-title {
    font-size: 1.5rem;
  }
}

@media (max-width: 480px) {
  .cart-main {
    padding-top: 80px;
  }
  
  .cart-container {
    padding: 0 15px;
  }
  
  .cart-title {
    font-size: 2rem;
    letter-spacing: 3px;
  }
  
  .cart-count {
    font-size: 0.9rem;
  }
  
  .cart-item {
    padding: 20px;
    gap: 15px;
    border-radius: 16px;
  }
  
  .item-image {
    width: 75px;
    height: 75px;
    border-radius: 12px;
  }
  
  .item-name {
    font-size: 1.15rem;
    margin-bottom: 5px;
  }
  
  .item-category {
    font-size: 0.85rem;
  }
  
  .quantity-control {
    padding: 8px 12px;
    gap: 10px;
    background: rgba(0, 0, 0, 0.3);
    border-radius: 12px;
    width: fit-content;
  }
  
  .qty-btn {
    width: 38px;
    height: 38px;
    font-size: 1.3rem;
    border-radius: 10px;
  }
  
  .qty-value {
    font-size: 1.2rem;
    min-width: 32px;
    font-weight: 700;
  }
  
  .item-price {
    margin-left: auto;
  }
  
  .price-value {
    font-size: 1.25rem;
  }
  
  .btn-remove {
    width: 40px;
    height: 40px;
  }
  
  .btn-remove svg {
    width: 18px;
    height: 18px;
  }
  
  .btn-clear {
    padding: 12px 18px;
    font-size: 0.8rem;
  }
  
  .payment-card {
    padding: 20px;
  }
  
  .btn-checkout {
    padding: 14px;
    font-size: 0.95rem;
  }
  
  .balance-row {
    flex-direction: column;
    gap: 10px;
  }
  
  .btn-shop {
    padding: 14px 28px;
    font-size: 0.9rem;
  }
  
  .success-modal {
    width: 90%;
    max-width: 360px;
    padding: 30px 20px;
  }
  
  .success-title {
    font-size: 1.3rem;
  }
  
  .success-actions {
    flex-direction: column;
  }
  
  .btn-continue, .btn-profile {
    width: 100%;
    justify-content: center;
  }
}

/* Success Modal */
.success-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
}

.success-modal {
  position: relative;
  background: linear-gradient(165deg, rgba(12, 18, 30, 0.98) 0%, rgba(5, 10, 20, 0.99) 100%);
  border: 1px solid rgba(0, 255, 100, 0.3);
  border-radius: 24px;
  padding: 50px 40px;
  max-width: 480px;
  width: 100%;
  text-align: center;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.5), 0 0 60px rgba(0, 255, 100, 0.15);
}

.success-glow {
  position: absolute;
  top: -50%;
  left: 50%;
  transform: translateX(-50%);
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(0, 255, 100, 0.08) 0%, transparent 50%);
  pointer-events: none;
}

.success-corners {
  position: absolute;
  inset: 15px;
  pointer-events: none;
}

.success-corners .corner {
  position: absolute;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(0, 255, 100, 0.5);
}

.success-corners .corner.tl { top: 0; left: 0; border-right: none; border-bottom: none; }
.success-corners .corner.tr { top: 0; right: 0; border-left: none; border-bottom: none; }
.success-corners .corner.bl { bottom: 0; left: 0; border-right: none; border-top: none; }
.success-corners .corner.br { bottom: 0; right: 0; border-left: none; border-top: none; }

.success-icon {
  width: 90px;
  height: 90px;
  margin: 0 auto 25px;
  background: linear-gradient(135deg, rgba(0, 255, 100, 0.15) 0%, rgba(0, 200, 80, 0.08) 100%);
  border: 2px solid rgba(0, 255, 100, 0.4);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: pulse-success 2s ease-in-out infinite;
}

.success-icon svg {
  width: 45px;
  height: 45px;
  color: #00ff64;
  filter: drop-shadow(0 0 15px rgba(0, 255, 100, 0.5));
}

@keyframes pulse-success {
  0%, 100% { box-shadow: 0 0 0 0 rgba(0, 255, 100, 0.4); }
  50% { box-shadow: 0 0 0 15px rgba(0, 255, 100, 0); }
}

.success-title {
  font-family: var(--font-display);
  font-size: 1.8rem;
  color: #fff;
  letter-spacing: 4px;
  margin-bottom: 15px;
  text-shadow: 0 0 30px rgba(0, 255, 100, 0.3);
}

.success-message {
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 8px;
}

.success-message .highlight {
  color: #00ff64;
  font-weight: 700;
  font-size: 1.3rem;
}

.success-sub {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 30px;
}

.success-actions {
  display: flex;
  gap: 15px;
  justify-content: center;
}

.btn-continue, .btn-profile {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 24px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 0.85rem;
  letter-spacing: 1px;
  text-decoration: none;
  transition: all 0.3s ease;
  cursor: pointer;
}

.btn-continue {
  background: linear-gradient(135deg, var(--color-primary) 0%, #0095b3 100%);
  color: #000;
  border: none;
}

.btn-continue:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 30px rgba(0, 212, 255, 0.3);
}

.btn-profile {
  background: transparent;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.btn-profile:hover {
  border-color: rgba(0, 212, 255, 0.5);
  background: rgba(0, 212, 255, 0.1);
}

.btn-continue svg, .btn-profile svg {
  width: 18px;
  height: 18px;
}

.btn-close-modal {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.btn-close-modal:hover {
  background: rgba(255, 100, 100, 0.1);
  border-color: rgba(255, 100, 100, 0.3);
  color: #ff6b6b;
}

.btn-close-modal svg {
  width: 18px;
  height: 18px;
}

/* Modal Transition */
.modal-enter-active, .modal-leave-active {
  transition: all 0.3s ease;
}

.modal-enter-from, .modal-leave-to {
  opacity: 0;
}

.modal-enter-from .success-modal, .modal-leave-to .success-modal {
  transform: scale(0.9) translateY(20px);
}

.modal-enter-active .success-modal, .modal-leave-active .success-modal {
  transition: all 0.3s ease;
}

/* Checkout Modal */
.checkout-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
}

.checkout-modal {
  border-radius: 24px;
  background: linear-gradient(165deg, rgba(12, 18, 30, 0.98) 0%, rgba(5, 10, 20, 0.99) 100%);
  border: 1px solid rgba(0, 212, 255, 0.25);
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.6), 0 0 80px rgba(0, 212, 255, 0.15);
  padding: 20px 24px 24px;
  color: #fff;
  max-width: 680px;
  width: 100%;
  animation: checkoutIn 0.3s ease;
  overflow: hidden;
}

@keyframes checkoutIn {
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.checkout-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  padding-bottom: 14px;
  border-bottom: 1px solid rgba(255,255,255,0.08);
}

.checkout-back {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,0.12);
  background: rgba(255,255,255,0.06);
  cursor: pointer;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease, background 0.15s ease;
}

.checkout-back svg {
  width: 18px;
  height: 18px;
}

.checkout-back:hover {
  transform: translateY(-1px);
  background: rgba(255,255,255,0.10);
}

.checkout-head-title {
  flex: 1;
  text-align: center;
  font-family: var(--font-display);
  font-weight: 900;
  letter-spacing: 2px;
  font-size: 1.05rem;
  color: rgba(255,255,255,0.92);
  text-transform: uppercase;
}

.checkout-x {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,0.12);
  background: rgba(255,255,255,0.06);
  cursor: pointer;
  font-size: 20px;
  font-weight: 300;
  line-height: 1;
  color: rgba(255,255,255,0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease, background 0.15s ease;
}

.checkout-x:hover {
  transform: translateY(-1px);
  background: rgba(255,255,255,0.10);
}

.checkout-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  max-width: 640px;
  margin: 0 auto;
}

.checkout-field {
  text-align: left;
}

.checkout-field label {
  display: block;
  color: rgba(255,255,255,0.70);
  font-weight: 800;
  font-size: 0.8rem;
  margin-bottom: 6px;
  letter-spacing: 0.5px;
}

.checkout-input {
  width: 100%;
  padding: 12px 14px;
  background: rgba(0, 0, 0, 0.45);
  border: 1px solid rgba(255,255,255,0.12);
  color: #fff;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 650;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.checkout-input::placeholder {
  color: rgba(255,255,255,0.28);
}

.checkout-input:focus {
  outline: none;
  border-color: rgba(0, 212, 255, 0.55);
  box-shadow: 0 0 0 4px rgba(0, 212, 255, 0.12);
}

.checkout-agree {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  margin-top: 16px;
  user-select: none;
}

.checkout-agree input {
  display: none;
}

.checkout-agree .check {
  width: 22px;
  height: 22px;
  border-radius: 7px;
  border: 2px solid rgba(255,255,255,0.18);
  background: rgba(0,0,0,0.25);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.checkout-agree input:checked + .check {
  background: var(--color-primary);
  border-color: var(--color-primary);
}

.checkout-agree input:checked + .check::after {
  content: '✓';
  color: #000;
  font-weight: 900;
  font-size: 14px;
}

.checkout-agree .text {
  color: rgba(255,255,255,0.7);
  font-size: 0.85rem;
  line-height: 1.4;
}

.checkout-agree .text a {
  position: relative;
  z-index: 2;
  color: var(--accent, #dfc166);
  font-weight: 700;
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 3px;
  pointer-events: auto;
  transition: color 0.18s ease, opacity 0.18s ease;
}

.checkout-agree .text a:hover,
.checkout-agree .text a:focus-visible {
  color: #fff2ad;
  opacity: 0.9;
}

.checkout-actions {
  margin-top: 20px;
  text-align: center;
}

.checkout-total {
  font-family: var(--font-display);
  font-weight: 900;
  letter-spacing: 2px;
  font-size: 1.45rem;
  color: #fff;
  margin-bottom: 16px;
}

.checkout-pay {
  width: 100%;
  padding: 16px 18px;
  border-radius: 16px;
  border: none;
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.50), rgba(0, 163, 204, 0.22));
  color: rgba(255,255,255,0.95);
  font-weight: 900;
  letter-spacing: 2px;
  cursor: pointer;
  transition: transform 0.2s ease, filter 0.2s ease;
  box-shadow: 0 10px 30px rgba(0, 212, 255, 0.12);
}

.checkout-pay:hover:not(:disabled) {
  transform: translateY(-1px);
  filter: brightness(1.05);
}

.checkout-pay:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.checkout-methods {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.checkout-methods .checkout-pay {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.checkout-method-logos {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.checkout-method-logo {
  height: 22px;
  width: auto;
  object-fit: contain;
}

.checkout-method-name {
  letter-spacing: 2px;
}

.checkout-insufficient {
  margin-top: 14px;
  padding: 12px 16px;
  background: rgba(255, 100, 100, 0.10);
  border: 1px solid rgba(255, 100, 100, 0.25);
  border-radius: 10px;
  color: #ff6b6b;
  font-size: 0.85rem;
}

.checkout-divider {
  margin: 14px 0;
  color: rgba(255,255,255,0.4);
  font-size: 0.85rem;
}

.checkout-balance {
  width: 100%;
  padding: 16px 18px;
  border-radius: 14px;
  border: none;
  background: linear-gradient(135deg, var(--color-primary) 0%, #0095b3 100%);
  color: #000;
  font-weight: 900;
  letter-spacing: 2px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 6px 25px rgba(0, 212, 255, 0.3);
}

.checkout-balance:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 35px rgba(0, 212, 255, 0.4);
}

.checkout-balance:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.checkout-error {
  margin-top: 14px;
  padding: 12px 16px;
  background: rgba(255, 100, 100, 0.12);
  border: 1px solid rgba(255, 100, 100, 0.3);
  border-radius: 10px;
  color: #ff6b6b;
  font-size: 0.85rem;
}

.modal-enter-from .checkout-modal, .modal-leave-to .checkout-modal {
  transform: scale(0.9) translateY(20px);
}

.modal-enter-active .checkout-modal, .modal-leave-active .checkout-modal {
  transition: all 0.3s ease;
}

@media (max-width: 600px) {
  .checkout-modal {
    padding: 16px;
  }
  
  .checkout-grid {
    grid-template-columns: 1fr;
  }
  
  .checkout-head-title {
    font-size: 0.9rem;
  }
  
  .checkout-total {
    font-size: 1.2rem;
  }
}

/* ── Steam Required Modal ── */
.steam-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.steam-modal {
  background: linear-gradient(135deg, #1a1a1a 0%, #0d0d0d 100%);
  border: 2px solid #333;
  border-radius: 16px;
  padding: 2rem;
  width: 100%;
  max-width: 420px;
  position: relative;
  text-align: center;
}

.steam-modal-close {
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  color: #666;
  font-size: 28px;
  cursor: pointer;
  line-height: 1;
  transition: color 0.2s;
}

.steam-modal-close:hover { color: var(--color-primary); }

.steam-icon-wrap {
  position: relative;
  width: 72px;
  height: 72px;
  margin: 0.25rem auto 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.steam-icon-glow {
  position: absolute;
  inset: -10px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(var(--color-primary-rgb), 0.35), transparent 70%);
  filter: blur(14px);
  pointer-events: none;
  animation: steam-pulse 2.4s ease-in-out infinite;
}

@keyframes steam-pulse {
  0%, 100% { opacity: 0.6; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.08); }
}

.steam-icon {
  position: relative;
  width: 56px;
  height: 56px;
  filter: drop-shadow(0 0 10px rgba(var(--color-primary-rgb), 0.5));
}

.steam-modal-title {
  font-family: var(--font-display);
  font-size: 1.5rem;
  color: var(--color-primary);
  margin: 0 0 0.75rem;
  letter-spacing: 0.02em;
}

.steam-modal-desc {
  color: rgba(255, 255, 255, 0.65);
  font-size: 0.9rem;
  line-height: 1.6;
  margin: 0 0 1.5rem;
}

.steam-modal-actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.steam-modal-confirm {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
  border: none;
  border-radius: 8px;
  color: #000;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.2s;
}

.steam-modal-confirm:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 20px rgba(var(--color-primary-rgb), 0.3);
}

.btn-steam-icon {
  width: 18px;
  height: 18px;
  filter: brightness(0);
}

.steam-modal-cancel {
  width: 100%;
  padding: 12px;
  background: transparent;
  border: 1px solid #555;
  border-radius: 8px;
  color: #888;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.steam-modal-cancel:hover {
  border-color: #888;
  color: #fff;
}
</style>
